import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErrorApi, api } from '../api/cliente';
import type { Caracteristica, Grupo, TipoInmueble, TipoValor } from '../api/tipos';
import { useSesion } from '../auth/sesion';
import {
  AreaTexto,
  Aviso,
  Campo,
  Cargando,
  Casilla,
  Encabezado,
  Etiqueta,
  Seleccion,
  Tarjeta,
  Vacio,
} from '../ui/piezas';

const TIPOS_VALOR: { valor: TipoValor; texto: string; ayuda: string }[] = [
  { valor: 'BOOLEANO', texto: 'Sí / no', ayuda: 'Piscina, ascensor, portería' },
  { valor: 'NUMERICO', texto: 'Número', ayuda: 'Parqueaderos de visitantes, metros de terraza' },
  { valor: 'TEXTO', texto: 'Texto libre', ayuda: 'Vista al oriente. No se puede filtrar bien' },
  { valor: 'OPCION', texto: 'Lista de opciones', ayuda: 'Tipo de techo, acabado de cocina' },
];

interface Borrador {
  nombre: string;
  grupo: string;
  tipoValor: TipoValor;
  unidad: string;
  ayuda: string;
  filtrable: boolean;
  orden: string;
  tipos: string[];
  opciones: string;
}

const VACIO: Borrador = {
  nombre: '',
  grupo: '',
  tipoValor: 'BOOLEANO',
  unidad: '',
  ayuda: '',
  filtrable: true,
  orden: '100',
  tipos: [],
  opciones: '',
};

/**
 * El catálogo que dibuja el formulario de carga.
 *
 * Es la pantalla que hace cierta la promesa del modelo: todos los inmuebles son
 * distintos y siempre va a faltar algo, y lo que falte se agrega aquí en vez de
 * en una migración.
 */
export default function Catalogo() {
  const cache = useQueryClient();
  const { puede } = useSesion();
  const [forma, setForma] = useState<Borrador>(VACIO);
  const [editando, setEditando] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);
  const [filtroTipo, setFiltroTipo] = useState('');

  const grupos = useQuery({
    queryKey: ['grupos'],
    queryFn: () => api<Grupo[]>('/admin/catalogo/grupos'),
  });
  const tipos = useQuery({
    queryKey: ['tipos-inmueble'],
    queryFn: () => api<TipoInmueble[]>('/admin/catalogo/tipos-inmueble'),
  });
  const lista = useQuery({
    queryKey: ['caracteristicas-admin', filtroTipo],
    queryFn: () =>
      api<Caracteristica[]>(
        filtroTipo
          ? `/admin/catalogo/caracteristicas?tipo=${filtroTipo}`
          : '/admin/catalogo/caracteristicas?activas=false',
      ),
  });

  const limpiar = () => {
    setForma(VACIO);
    setEditando(null);
  };

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        nombre: forma.nombre,
        grupo: forma.grupo,
        tipoValor: forma.tipoValor,
        unidad: forma.unidad || null,
        ayuda: forma.ayuda || null,
        filtrable: forma.filtrable,
        orden: Number(forma.orden) || 0,
        tipos: forma.tipos,
        opciones: forma.opciones
          .split('\n')
          .map((o) => o.trim())
          .filter(Boolean),
      };
      return editando
        ? api<Caracteristica>(`/admin/catalogo/caracteristicas/${editando}`, {
            metodo: 'PUT',
            cuerpo,
          })
        : api<Caracteristica>('/admin/catalogo/caracteristicas', { metodo: 'POST', cuerpo });
    },
    onSuccess: () => {
      setError(null);
      setExito(editando ? 'Actualizada.' : 'Agregada. Ya aparece en el formulario de carga.');
      limpiar();
      void cache.invalidateQueries({ queryKey: ['caracteristicas-admin'] });
      void cache.invalidateQueries({ queryKey: ['caracteristicas'] });
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const desactivar = useMutation({
    mutationFn: (id: string) =>
      api<Caracteristica>(`/admin/catalogo/caracteristicas/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['caracteristicas-admin'] });
      void cache.invalidateQueries({ queryKey: ['caracteristicas'] });
    },
    onError: setError,
  });

  const editar = (c: Caracteristica) => {
    setEditando(c.id);
    setForma({
      nombre: c.nombre,
      grupo: c.grupo,
      tipoValor: c.tipoValor,
      unidad: c.unidad ?? '',
      ayuda: c.ayuda ?? '',
      filtrable: c.filtrable,
      orden: String(c.orden),
      tipos: c.tipos,
      opciones: c.opciones.join('\n'),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const errorApi = error instanceof ErrorApi ? error : null;
  const editable = puede('configuracion:editar');

  return (
    <>
      <Encabezado
        titulo="Catálogo"
        nota="Lo que se nos pasó por alto se agrega aquí, no en una migración."
      />

      <Aviso error={error} exito={exito} />

      {editable && (
        <Tarjeta titulo={editando ? 'Editar característica' : 'Agregar característica'}>
          <div className="rejilla">
            <Campo
              etiqueta="Nombre"
              requerido
              valor={forma.nombre}
              alCambiar={(v) => setForma({ ...forma, nombre: v })}
              error={errorApi?.mensajeDe('nombre')}
              placeholder="Coworking"
            />
            <Seleccion
              etiqueta="Grupo"
              requerido
              ayuda="La pestaña de la ficha donde se pinta"
              valor={forma.grupo}
              alCambiar={(v) => setForma({ ...forma, grupo: v })}
              vacio="Elige uno"
              opciones={(grupos.data ?? []).map((g) => ({ valor: g.slug, texto: g.nombre }))}
            />
            <Seleccion
              etiqueta="Qué guarda"
              valor={forma.tipoValor}
              alCambiar={(v) => setForma({ ...forma, tipoValor: v as TipoValor })}
              ayuda={TIPOS_VALOR.find((t) => t.valor === forma.tipoValor)?.ayuda}
              opciones={TIPOS_VALOR.map((t) => ({ valor: t.valor, texto: t.texto }))}
            />
            {forma.tipoValor === 'NUMERICO' && (
              <Campo
                etiqueta="Unidad"
                ayuda="Para escribir «Terraza · 18 m²» sin teclearlo cada vez"
                valor={forma.unidad}
                alCambiar={(v) => setForma({ ...forma, unidad: v })}
                placeholder="m²"
              />
            )}
            <Campo
              etiqueta="Orden"
              valor={forma.orden}
              alCambiar={(v) => setForma({ ...forma, orden: v })}
            />
          </div>

          {forma.tipoValor === 'OPCION' && (
            <>
              <div style={{ height: 12 }} />
              <AreaTexto
                etiqueta="Valores admitidos (uno por línea)"
                requerido
                ayuda="Sin la lista, cada asesor escribe algo distinto y después no se puede filtrar"
                valor={forma.opciones}
                alCambiar={(v) => setForma({ ...forma, opciones: v })}
                filas={4}
              />
            </>
          )}

          <div style={{ height: 12 }} />
          <AreaTexto
            etiqueta="Texto de ayuda"
            ayuda="Qué se considera «vista panorámica». Lo ve quien carga el inmueble"
            valor={forma.ayuda}
            alCambiar={(v) => setForma({ ...forma, ayuda: v })}
            filas={2}
          />

          <div style={{ height: 12 }} />
          <label>Aplica a</label>
          <p className="campo__ayuda" style={{ marginTop: 0 }}>
            Sin marcar nada, aplica a todos los tipos. Marcar acota: «aire acondicionado» no le
            aplica a un lote.
          </p>
          <div className="fila" style={{ marginTop: 8 }}>
            {(tipos.data ?? []).map((t) => (
              <label key={t.id} className="campo__casilla" style={{ marginRight: 12 }}>
                <input
                  type="checkbox"
                  checked={forma.tipos.includes(t.id)}
                  onChange={(e) =>
                    setForma({
                      ...forma,
                      tipos: e.target.checked
                        ? [...forma.tipos, t.id]
                        : forma.tipos.filter((x) => x !== t.id),
                    })
                  }
                />
                <span style={{ fontSize: '0.85rem' }}>{t.nombre}</span>
              </label>
            ))}
          </div>

          <div style={{ height: 12 }} />
          <Casilla
            etiqueta="Se puede filtrar por ella en el portal"
            valor={forma.filtrable}
            alCambiar={(v) => setForma({ ...forma, filtrable: v })}
          />

          <div className="fila fila--fin" style={{ marginTop: 14 }}>
            {editando && (
              <button className="secundario" onClick={limpiar}>
                Cancelar
              </button>
            )}
            <button
              disabled={guardar.isPending || !forma.nombre || !forma.grupo}
              onClick={() => guardar.mutate()}
            >
              {editando ? 'Guardar cambios' : 'Agregar'}
            </button>
          </div>
        </Tarjeta>
      )}

      <Tarjeta titulo="Características">
        <div style={{ maxWidth: 320, marginBottom: 14 }}>
          <Seleccion
            etiqueta="Ver las que aplican a"
            valor={filtroTipo}
            alCambiar={setFiltroTipo}
            vacio="Todas, incluidas las inactivas"
            opciones={(tipos.data ?? []).map((t) => ({ valor: t.id, texto: t.nombre }))}
          />
        </div>

        {lista.isPending ? (
          <Cargando />
        ) : (lista.data ?? []).length === 0 ? (
          <Vacio>No hay características para ese tipo.</Vacio>
        ) : (
          <div className="tabla-marco">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Grupo</th>
                  <th>Guarda</th>
                  <th>Aplica a</th>
                  <th>Estado</th>
                  {editable && <th />}
                </tr>
              </thead>
              <tbody>
                {(lista.data ?? []).map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div>{c.nombre}</div>
                      {c.opciones.length > 0 && (
                        <small style={{ opacity: 0.7 }}>{c.opciones.join(' · ')}</small>
                      )}
                    </td>
                    <td>{c.grupoNombre}</td>
                    <td>
                      {TIPOS_VALOR.find((t) => t.valor === c.tipoValor)?.texto}
                      {c.unidad && ` · ${c.unidad}`}
                    </td>
                    <td>
                      {c.tipos.length === 0
                        ? 'Todos los tipos'
                        : c.tipos
                            .map((id) => tipos.data?.find((t) => t.id === id)?.nombre ?? '—')
                            .join(', ')}
                    </td>
                    <td>
                      <Etiqueta>{c.activa ? 'ACTIVO' : 'ARCHIVADO'}</Etiqueta>
                    </td>
                    {editable && (
                      <td>
                        <button className="plano" onClick={() => editar(c)}>
                          Editar
                        </button>
                        {c.activa && (
                          <button className="plano" onClick={() => desactivar.mutate(c.id)}>
                            Desactivar
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="campo__ayuda" style={{ marginTop: 12 }}>
          No se borran: borrar una arrastraría su valor en cada inmueble que la tuviera.
        </p>
      </Tarjeta>
    </>
  );
}
